import type { OpenMetadataHit, SearchResult } from '@/entities/search';

import { filterAndMapTags } from './tagUtils';

type SearchResultType = SearchResult['type'];

/**
 * OpenMetadata Elasticsearch 검색 결과를 애플리케이션의 SearchResult 형식으로 변환합니다.
 *
 * @description
 * OpenMetadata의 검색 API는 Elasticsearch 형식의 응답을 반환합니다.
 * 이 함수는 원본 `_source` 데이터를 포함하여 UI에서 사용하기 적합한 형태로 변환합니다.
 *
 * 주요 변환 작업:
 * - FQN을 사람이 읽기 쉬운 경로로 변환
 * - Tier 태그를 일반 태그 목록에서 분리
 * - 타임스탬프를 원본 형식으로 유지 (포맷팅은 UI 레이어에서 처리)
 * - 소유자 정보 추출 (첫 번째 소유자 우선)
 *
 * @example
 * ```typescript
 * const hit: OpenMetadataHit = {
 *   _index: 'table_search_index',
 *   _id: '123',
 *   _score: 1.0,
 *   _source: {
 *     id: '123',
 *     entityType: 'table',
 *     displayName: 'Users Table',
 *     // ... other fields
 *   }
 * };
 *
 * const result = mapOpenMetadataToSearchResult(hit);
 * // Returns: SearchResult with formatted data
 * ```
 *
 * @param hit - OpenMetadata Elasticsearch 검색 결과 hit 객체
 * @returns 변환된 SearchResult 객체 (원본 _source 데이터 포함)
 */
export const mapOpenMetadataToSearchResult = (hit: OpenMetadataHit): SearchResult => {
  const source = hit._source;

  const tags = filterAndMapTags(source.tags);

  return {
    id: source.id,
    type: source.entityType as SearchResultType,
    title: source.displayName || source.name,
    path: source.fullyQualifiedName,
    owner:
      source.owners && source.owners.length > 0
        ? {
            name: source.owners[0].displayName,
            department: source.owners[0].type,
          }
        : { name: '없음' },
    updatedAt: source.updatedAt,
    tags,
    tier: source.tier
      ? {
          tagFQN: source.tier.tagFQN,
          name: source.tier.name,
          description: source.tier.description || '',
        }
      : undefined,
    certification:
      source.certification?.tagLabel?.name && source.certification.tagLabel.tagFQN
        ? {
            name: source.certification.tagLabel.name,
            tagFQN: source.certification.tagLabel.tagFQN,
            description: source.certification.tagLabel.description || undefined,
            expiryDate: source.certification.expiryDate,
            appliedDate: source.certification.appliedDate,
            style: source.certification.tagLabel.style || undefined,
          }
        : undefined,
    description: source.description || source.service?.description || '',
    serviceType: source.serviceType,
    database: source.database,
    databaseSchema: source.databaseSchema,
    service: source.service,
    domains: source.domains,
    _source: source,
  };
};
